// @flow

import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { Text } from 'reshaped';

import type { ProductType } from '@gandi/cart';
import {
  ProductDomain,
  ProductCloud,
  ProductWebhosting,
  ProductCertificate,
  ProductMailbox,
  ProductOther,
} from '@gandi/icons';
import { Icon } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import {
  selectCertificateURL,
  selectGostackURL,
  getLinkTarget,
} from 'src/modules/config';

import styles from './ProductTile.scss';

// Types needed in app
export type Type =
  | 'domain'
  | 'acme'
  | 'certificate'
  | 'paas'
  | 'cloud'
  | 'mailbox-standalone';

type Props = {
  type: Type,
};

function useProductNames(type: Type) {
  const { __ } = useTranslator();
  const names = {
    domain: __('Domain'),
    acme: __('ACME certificates'),
    certificate: __('SSL Certificate'),
    paas: __('Web Hosting'),
    cloud: __('Cloud'),
    'mailbox-standalone': __('Mailbox'),
  };
  return names[type];
}

const products = new Map([
  ['domain', { name: 'domain', icon: ProductDomain }],
  ['acme', { name: 'certificate', icon: ProductCertificate }],
  ['certificate', { name: 'certificate', icon: ProductCertificate }],
  ['mailbox-standalone', { name: 'mailbox', icon: ProductMailbox }],
  ['mailboxv2', { name: 'mailbox', icon: ProductMailbox }],
  ['paas', { name: 'webhosting', icon: ProductWebhosting }],
  ['cloud', { name: 'cloud', icon: ProductCloud }],
  ['marketplace', { name: 'other', icon: ProductOther }],
  ['corporate', { name: 'other', icon: ProductOther }],
  ['tmch', { name: 'other', icon: ProductOther }],
  ['domain_option', { name: 'other', icon: ProductOther }],
  ['other', { name: 'other', icon: ProductOther }],
]);

function ProductLink({
  type,
  children,
  className,
}: {
  type: Type,
  children: React.Node,
  className?: string,
}): React.MixedElement {
  const title = useProductNames(type);

  const certificateUrl = useAppSelector(selectCertificateURL);
  const gostackUrl = useAppSelector(selectGostackURL);
  const links: { [ProductType]: { to: string } | { href: string } } = {
    domain: { to: '/domain/suggest' },
    acme: { to: '/certificate/acme' },
    certificate: getLinkTarget(certificateUrl),
    paas: { to: '/simplehosting/create' },
    cloud: { href: gostackUrl },
    'mailbox-standalone': { to: '/mailboxes/create' },
  };

  const props = {
    title,
    className,
  };

  const linkType = links[type];

  if (linkType.to) {
    return (
      <Link {...props} to={linkType.to}>
        {children}
      </Link>
    );
  }

  return (
    <a {...props} href={linkType.href}>
      {children}
    </a>
  );
}

export function ProductTile({
  type: initialType,
}: Props): React.MixedElement | null {
  const FF_SHOP_ACME = useFeatureFlag('FF_SHOP_ACME');
  const type =
    initialType === 'certificate' && FF_SHOP_ACME
      ? ('acme' as Type)
      : initialType;

  const productName = useProductNames(type);

  if (!productName) {
    return null;
  }

  const product = products.get(type || 'other') as any as {
    name: string,
    icon: React.ComponentType<any>,
  };

  return (
    <ProductLink
      type={type}
      className={clsx(styles.root, styles[product.name])}
    >
      <Icon svg={product.icon} size={5} />
      <Text
        as="p"
        variant="body-2"
        weight="medium"
        align="center"
        color="neutral"
      >
        {productName}
      </Text>
    </ProductLink>
  );
}
